
import React, { useState, useEffect } from 'react';
import ListTextarea from '../../../components/ui/ListTextarea';
import { supabase } from '../../../lib/supabase/client';
import { Send, Lock, Unlock, Loader2, Trash2, ArrowRight, Edit3, Paperclip, CheckCircle2, AlertCircle } from 'lucide-react';

interface Kommentar {
  id: string;
  ticket_id: string;
  autor_id: string | null;
  inhalt: string;
  ist_intern: boolean;
  parent_id: string | null;
  created_at: string;
  autor?: { full_name: string; avatar_url: string | null } | null;
}

type CommentNode = Kommentar & { children: CommentNode[] };

function buildTree(kommentare: Kommentar[]): CommentNode[] {
  const map = new Map<string, CommentNode>();
  kommentare.forEach(k => map.set(k.id, { ...k, children: [] }));
  const roots: CommentNode[] = [];
  map.forEach(node => {
    if (node.parent_id && map.has(node.parent_id)) {
      map.get(node.parent_id)!.children.push(node);
    } else {
      roots.push(node);
    }
  });
  return roots;
}

export interface HistorieEintrag {
  id: string;
  ticket_id: string;
  benutzer_id: string | null;
  aktion: string;
  alter_wert: string | null;
  neuer_wert: string | null;
  created_at: string;
  benutzer?: { full_name: string } | null;
}

const STATUS_LABELS: Record<string, string> = {
  neu: 'Neu', offen: 'Offen', in_bearbeitung: 'In Bearbeitung',
  warten_auf_rueckmeldung: 'Rückmeldung', warten_auf_dienstleister: 'Dienstleister',
  eskaliert: 'Eskaliert', erledigt: 'Erledigt', geschlossen: 'Geschlossen',
};

export function formatTime(date: string) {
  const d = new Date(date);
  return `${d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })} ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
}

function getInitials(name?: string) {
  if (!name) return '??';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
}

export function getHistorieInfo(h: HistorieEintrag): { icon: React.ReactNode; label: string; detail?: string } {
  switch (h.aktion) {
    case 'status_change': {
      const from = STATUS_LABELS[h.alter_wert || ''] || h.alter_wert || '–';
      const to = STATUS_LABELS[h.neuer_wert || ''] || h.neuer_wert || '–';
      return {
        icon: <ArrowRight size={13} className="text-blue-500" />,
        label: 'Status geändert',
        detail: `${from} → ${to}`,
      };
    }
    case 'bearbeitet':
      return {
        icon: <Edit3 size={13} className="text-violet-500" />,
        label: 'Ticket bearbeitet',
        detail: h.neuer_wert || undefined,
      };
    case 'kommentar_geloescht':
      return {
        icon: <Trash2 size={13} className="text-red-400" />,
        label: 'Kommentar gelöscht',
      };
    case 'anhang_hochgeladen':
      return {
        icon: <Paperclip size={13} className="text-[#1a1d24]" />,
        label: 'Anhang hochgeladen',
        detail: h.neuer_wert || undefined,
      };
    case 'anhang_geloescht':
      return {
        icon: <Trash2 size={13} className="text-red-400" />,
        label: 'Anhang gelöscht',
        detail: h.neuer_wert || undefined,
      };
    case 'erledigt':
    case 'geschlossen':
      return {
        icon: <CheckCircle2 size={13} className="text-emerald-500" />,
        label: h.neuer_wert || 'Ticket abgeschlossen',
      };
    default:
      return {
        icon: <AlertCircle size={13} className="text-gray-400" />,
        label: h.neuer_wert || h.aktion,
      };
  }
}

interface CommentThreadProps {
  node: CommentNode;
  depth: number;
  replyingToId: string | null;
  replyText: string;
  sending: boolean;
  userProfile?: any;
  onReplyToggle: (id: string | null) => void;
  onReplyTextChange: (text: string) => void;
  onSend: (parentId: string) => void;
  onDelete: (id: string) => void;
  onToggleCheckbox: (commentId: string, lineIdx: number, checked: boolean) => void;
}

const CommentThread: React.FC<CommentThreadProps> = ({
  node, depth, replyingToId, replyText, sending, userProfile,
  onReplyToggle, onReplyTextChange, onSend, onDelete, onToggleCheckbox,
}) => {
  if (depth > 12) return null;
  const indent = Math.min(depth, 8) * 16;

  const renderContent = (inhalt: string, commentId: string) => {
    return inhalt.split('\n').map((line, idx) => {
      if (line.startsWith('☐ ') || line.startsWith('☑ ')) {
        const checked = line.startsWith('☑ ');
        return (
          <label key={idx} className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => onToggleCheckbox(commentId, idx, e.target.checked)}
              className="mt-0.5 accent-[#1a1d24]"
            />
            <span className={checked ? 'line-through text-gray-400 text-[11px] font-medium' : 'text-[11px] font-medium text-gray-700'}>
              {line.slice(2)}
            </span>
          </label>
        );
      }
      return <p key={idx} className="text-[11px] font-medium text-gray-700 leading-relaxed whitespace-pre-wrap">{line}</p>;
    });
  };

  return (
    <div style={{ marginLeft: indent }}>
      <div className={`rounded-xl p-3 ${node.ist_intern ? 'bg-amber-50/50 border border-amber-100' : 'bg-gray-50'}`}>
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-[#1a1d24] flex items-center justify-center text-white text-[7px] font-black">
              {getInitials(node.autor?.full_name)}
            </div>
            <span className="text-[10px] font-black text-gray-900">{node.autor?.full_name || 'Unbekannt'}</span>
            {node.ist_intern && (
              <span className="text-[7px] font-black text-amber-500 bg-amber-100 px-1.5 py-0.5 rounded uppercase tracking-wider flex items-center gap-0.5">
                <Lock size={8} /> Intern
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[8px] font-bold text-gray-400">{formatTime(node.created_at)}</span>
            {node.autor_id === userProfile?.id && (
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(node.id); }}
                className="text-gray-300 hover:text-red-500 transition-colors"
                title="Kommentar löschen"
              >
                <Trash2 size={12} />
              </button>
            )}
          </div>
        </div>
        <div className="space-y-0.5">
          {renderContent(node.inhalt, node.id)}
        </div>
        <button
          onClick={() => onReplyToggle(replyingToId === node.id ? null : node.id)}
          className="mt-2 text-[8px] font-black text-gray-400 hover:text-[#1a1d24] uppercase tracking-widest transition-colors"
        >
          Antworten
        </button>
      </div>

      {replyingToId === node.id && (
        <div className="mt-2 border border-gray-100 rounded-xl overflow-hidden">
          <ListTextarea
            value={replyText}
            onChange={(e) => onReplyTextChange(e.target.value)}
            placeholder="Antworten..."
            rows={2}
            className="w-full px-3 py-2.5 text-xs font-medium text-gray-900 placeholder:text-gray-300 focus:outline-none resize-none"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(node.id); }
              if (e.key === 'Escape') { onReplyToggle(null); onReplyTextChange(''); }
            }}
          />
          <div className="flex items-center justify-end gap-2 px-3 py-2 bg-gray-50 border-t border-gray-100">
            <button
              onClick={() => { onReplyToggle(null); onReplyTextChange(''); }}
              className="text-[8px] font-black text-gray-400 uppercase tracking-widest hover:text-gray-600 transition-colors"
            >
              Abbrechen
            </button>
            <button
              onClick={() => onSend(node.id)}
              disabled={!replyText.trim() || sending}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1a1d24] text-white rounded-lg text-[9px] font-black uppercase tracking-widest hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              Senden
            </button>
          </div>
        </div>
      )}

      {node.children.length > 0 && (
        <div className="mt-2 space-y-2">
          {node.children.map(child => (
            <CommentThread
              key={child.id}
              node={child}
              depth={depth + 1}
              replyingToId={replyingToId}
              replyText={replyText}
              sending={sending}
              userProfile={userProfile}
              onReplyToggle={onReplyToggle}
              onReplyTextChange={onReplyTextChange}
              onSend={onSend}
              onDelete={onDelete}
              onToggleCheckbox={onToggleCheckbox}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface TicketKommentareProps {
  ticketId: string;
  userProfile?: any;
  onCommentAdded?: () => void;
  mode?: 'kommentare' | 'verlauf';
}

const TicketKommentare: React.FC<TicketKommentareProps> = ({ ticketId, userProfile, onCommentAdded, mode = 'kommentare' }) => {
  const [kommentare, setKommentare] = useState<Kommentar[]>([]);
  const [historie, setHistorie] = useState<HistorieEintrag[]>([]);
  const [newComment, setNewComment] = useState('');
  const [isIntern, setIsIntern] = useState(false);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');

  useEffect(() => {
    fetchData();
  }, [ticketId]);

  const fetchData = async () => {
    setLoading(true);
    const [komRes, histRes] = await Promise.all([
      supabase
        .from('ticket_kommentare')
        .select('*, autor:profiles!ticket_kommentare_autor_id_fkey(full_name, avatar_url)')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true }),
      supabase
        .from('ticket_historie')
        .select('*, benutzer:profiles!ticket_historie_benutzer_id_fkey(full_name)')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true })
    ]);
    setKommentare(komRes.data || []);
    setHistorie(histRes.data || []);
    setLoading(false);
  };

  const handleDelete = async (kommentarId: string) => {
    try {
      await supabase.from('ticket_kommentare').delete().eq('id', kommentarId);
      await supabase.from('ticket_historie').insert({
        ticket_id: ticketId,
        benutzer_id: userProfile?.id,
        aktion: 'kommentar_geloescht',
        neuer_wert: 'Kommentar gelöscht',
      });
      fetchData();
      onCommentAdded?.();
    } catch (err) {
      console.error('Error deleting comment:', err);
    }
  };

  const handleSend = async (parentId?: string | null) => {
    const text = parentId ? replyText : newComment;
    if (!text.trim() || sending) return;
    setSending(true);
    const parentComment = parentId ? kommentare.find(k => k.id === parentId) : null;
    const effectiveIsIntern = parentId ? (parentComment?.ist_intern ?? false) : isIntern;
    try {
      await supabase.from('ticket_kommentare').insert({
        ticket_id: ticketId,
        autor_id: userProfile?.id,
        inhalt: text.trim(),
        ist_intern: effectiveIsIntern,
        parent_id: parentId ?? null,
      });

      await supabase.from('ticket_historie').insert({
        ticket_id: ticketId,
        benutzer_id: userProfile?.id,
        aktion: 'kommentar',
        neuer_wert: effectiveIsIntern ? 'Interner Kommentar hinzugefügt' : 'Kommentar hinzugefügt',
      });

      fetchData();
      onCommentAdded?.();
      if (parentId) {
        setReplyText('');
        setReplyingToId(null);
      } else {
        setNewComment('');
      }
    } catch (err) {
      console.error('Error sending comment:', err);
    } finally {
      setSending(false);
    }
  };

  const handleToggleCheckbox = async (commentId: string, lineIdx: number, checked: boolean) => {
    const comment = kommentare.find(k => k.id === commentId);
    if (!comment) return;
    const lines = comment.inhalt.split('\n');
    if (!lines[lineIdx]?.startsWith('☐ ') && !lines[lineIdx]?.startsWith('☑ ')) return;
    lines[lineIdx] = checked
      ? '☑ ' + lines[lineIdx].slice(2)
      : '☐ ' + lines[lineIdx].slice(2);
    const newInhalt = lines.join('\n');
    await supabase
      .from('ticket_kommentare')
      .update({ inhalt: newInhalt })
      .eq('id', commentId);
    setKommentare(prev =>
      prev.map(k => k.id === commentId ? { ...k, inhalt: newInhalt } : k)
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 size={20} className="animate-spin text-gray-300" />
      </div>
    );
  }

  // ── Verlauf-Modus ──────────────────────────────────────────
  if (mode === 'verlauf') {
    const verlaufItems = historie.filter(h => h.aktion !== 'kommentar');
    if (verlaufItems.length === 0) {
      return <p className="text-xs font-medium text-gray-300 text-center py-4">Kein Verlauf vorhanden</p>;
    }
    return (
      <div className="relative">
        {/* Vertikale Linie */}
        <div className="absolute left-[19px] top-0 bottom-0 w-px bg-gray-100" />
        <div className="space-y-1">
          {verlaufItems.map((h) => {
            const info = getHistorieInfo(h);
            return (
              <div key={h.id} className="flex items-start gap-3 py-2.5 relative">
                <div className="w-10 h-10 rounded-full bg-white border border-gray-100 flex items-center justify-center shrink-0 z-10">
                  {info.icon}
                </div>
                <div className="flex-1 min-w-0 pt-1.5">
                  <p className="text-xs font-semibold text-gray-700">{info.label}</p>
                  {info.detail && (
                    <p className="text-[11px] font-medium text-gray-400 mt-0.5">{info.detail}</p>
                  )}
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] font-medium text-gray-400">{h.benutzer?.full_name || 'System'}</span>
                    <span className="text-[10px] text-gray-300">·</span>
                    <span className="text-[10px] font-medium text-gray-300">{formatTime(h.created_at)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Kommentare-Modus ────────────────────────────────────────
  const commentTree = buildTree(kommentare);

  return (
    <div className="space-y-3">
      {commentTree.length > 0 ? (
        <div className="space-y-2.5 max-h-[400px] overflow-y-auto custom-scrollbar pr-1">
          {commentTree.map(node => (
            <CommentThread
              key={`k-${node.id}`}
              node={node}
              depth={0}
              replyingToId={replyingToId}
              replyText={replyText}
              sending={sending}
              userProfile={userProfile}
              onReplyToggle={setReplyingToId}
              onReplyTextChange={setReplyText}
              onSend={handleSend}
              onDelete={handleDelete}
              onToggleCheckbox={handleToggleCheckbox}
            />
          ))}
        </div>
      ) : (
        <p className="text-xs font-medium text-gray-300 text-center py-4">Noch keine Kommentare</p>
      )}

      {/* Input */}
      <div className="border border-gray-100 rounded-xl overflow-hidden">
        <ListTextarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Kommentar schreiben..."
          rows={2}
          className="w-full px-3 py-2.5 text-xs font-medium text-gray-900 placeholder:text-gray-300 focus:outline-none resize-none"
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
        />
        <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-t border-gray-100">
          <button
            onClick={() => setIsIntern(!isIntern)}
            className={`flex items-center gap-1.5 text-[8px] font-black uppercase tracking-widest px-2.5 py-1 rounded-md transition-all ${
              isIntern ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-400 hover:text-gray-600'
            }`}
          >
            {isIntern ? <Lock size={10} /> : <Unlock size={10} />}
            {isIntern ? 'Intern' : 'Normal'}
          </button>
          <button
            onClick={() => handleSend()}
            disabled={!newComment.trim() || sending}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1a1d24] text-white rounded-lg text-[9px] font-black uppercase tracking-widest hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            Senden
          </button>
        </div>
      </div>
    </div>
  );
};

export default TicketKommentare;
